import React, { useEffect, useState } from 'react'
import { Box, Button, Typography, Paper } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { useUpdateInvitationStatusMutation, useGetInvitationMutation, useFetchWhiteLabelDetailsMutation } from 'src/services/white-label-team-management-service'

function TeamInvitation () {
  const [invitationStatus, setInvitationStatus] = useState('expiredURL')
  const [whiteLabelDetails, setWhiteLabelDetails] = useState(null)
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const id = searchParams.get('id')
  const token = searchParams.get('token')
  const partnerId = searchParams.get('partnerId')
  const [updateInvitationStatus] = useUpdateInvitationStatusMutation()
  const [getInvitation] = useGetInvitationMutation()
  const [fetchWhiteLabelDetails] = useFetchWhiteLabelDetailsMutation()
  const navigate = useNavigate()

  const getCurrentInvitation = async () => {
    const { data } = await getInvitation(token)

    if (data) {
      const { email, status } = data
      setInvitationStatus(status)
      const details = await fetchWhiteLabelDetails({ partnerId, email })
      setWhiteLabelDetails(details.data)
    }
  }

  const handleResponse = async (isAccepted) => {
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const authToken = window.localStorage.getItem('auth_token')
        if (authToken && isAccepted) {
          window.localStorage.removeItem('auth_token')
        }
      }
      await updateInvitationStatus({
        id,
        token,
        status: isAccepted ? 'accepted' : 'declined'
      })

      if (whiteLabelDetails?.isUserRegistered) {
        navigate('/login')
      } else {
        navigate(`/w-register?email=${email}`)
      }
    } catch (error) {
      toast.error(error?.data?.error?.message)
    }
  }

  useEffect(() => {
    if (token) {
      getCurrentInvitation()
    }
  }, [token])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center',
        bgcolor: '#f4f6f8'
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            borderRadius: 4,
            bgcolor: 'background.paper',
            textAlign: 'center'
          }}
        >

          {invitationStatus !== 'pending'
            ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <HourglassEmptyIcon sx={{ fontSize: 60, color: 'gray' }} />
                <Typography variant='h5' sx={{ mt: 2, color: 'gray' }}>
                  Invitation Expired
                </Typography>
                <Typography variant='body2' sx={{ mt: 1, color: 'gray' }}>
                  Please contact your administrator to request a new invitation.
                </Typography>
              </Box>
              )
            : (
              <>
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.1 }}
                  transition={{
                    repeat: Infinity,
                    repeatType: 'reverse',
                    duration: 1.5
                  }}
                >
                  <MailOutlineIcon sx={{ fontSize: 70, color: whiteLabelDetails?.partnerDetails?.theme?.colors?.main || '#216fed' }} />
                </motion.div>

                <Typography
                  variant='h4'
                  gutterBottom
                  sx={{ color: whiteLabelDetails?.partnerDetails?.theme?.colors?.main || '#216fed' }}
                >
                  {whiteLabelDetails?.partnerDetails?.theme?.companyName}
                </Typography>

                <Typography variant='body1' sx={{ mt: 2, color: '#333' }}>
                  <b>
                    {whiteLabelDetails?.partnerUserDetails?.name?.first}{' '}
                    {whiteLabelDetails?.partnerUserDetails?.name?.last || 'Super Admin'}
                  </b>{' '}
                  invited you to join a team.
                </Typography>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant='contained'
                    startIcon={<CheckCircleIcon />}
                    sx={{
                      bgcolor: whiteLabelDetails?.partnerDetails?.theme?.colors?.main || '#216fed',
                      '&:hover': { bgcolor: whiteLabelDetails?.partnerDetails?.theme?.colors?.dark || '#004EEB' }
                    }}
                    onClick={() => handleResponse(true)}
                  >
                    {whiteLabelDetails?.isUserRegistered ? 'Accept' : 'Accept & Create Account'}
                  </Button>
                  <Button
                    variant='contained'
                    startIcon={<CancelIcon />}
                    color='error'
                    onClick={() => handleResponse(false)}
                  >
                    Decline
                  </Button>
                </Box>
              </>
              )}
        </Paper>
      </motion.div>
    </Box>
  )
}

export default TeamInvitation
